/**
 * Parse the original image dimensions out of a Storyblok asset URL.
 *
 * Storyblok URLs look like:
 *   https://a.storyblok.com/f/<space>/<W>x<H>/<hash>/<filename>
 * The dimensions live at path index 5.
 *
 * Returns { width: 0, height: 0 } if the URL is empty or malformed,
 * which lets callers fall back to a 50/50 focal point.
 */
export function getStoryblokImageDimensions(url: string | null | undefined): {
  width: number;
  height: number;
} {
  if (!url) return { width: 0, height: 0 };

  const segment = url.split("/")[5] ?? "";
  const [w, h] = segment.split("x");

  return {
    width: parseInt(w ?? "0", 10) || 0,
    height: parseInt(h ?? "0", 10) || 0,
  };
}

/**
 * Convert a Storyblok focus string ("x1yY1:x2yY2", pixel coordinates)
 * into CSS object-position percentages.
 *
 * Uses the centre of the focal rectangle so that a rectangular selection
 * and a single focal point both resolve to the same visual centre.
 *
 * If `focus` is empty/missing or dimensions can't be derived, returns
 * the centre of the image (50%, 50%) — the same behaviour as the
 * default `object-position`.
 */
export function focusToObjectPosition(
  focus: string | null | undefined,
  src: string,
): { x: number; y: number } {
  if (!focus) return { x: 50, y: 50 };

  const [topLeft, bottomRight] = focus.split(":");
  const [x1Str, y1Str] = (topLeft ?? "").split("x");
  const [x2Str, y2Str] = (bottomRight ?? topLeft ?? "").split("x");

  const x1 = parseInt(x1Str ?? "0", 10);
  const y1 = parseInt(y1Str ?? "0", 10);
  const x2 = parseInt(x2Str ?? x1Str ?? "0", 10);
  const y2 = parseInt(y2Str ?? y1Str ?? "0", 10);

  const { width, height } = getStoryblokImageDimensions(src);
  if (!width || !height) return { x: 50, y: 50 };

  return {
    x: ((x1 + x2) / 2 / width) * 100,
    y: ((y1 + y2) / 2 / height) * 100,
  };
}

/** Detects whether a src URL is a Storyblok-hosted asset. */
export function isStoryblokAsset(src: string | null | undefined): boolean {
  if (!src || typeof src !== "string") return false;
  try {
    return new URL(src).hostname === "a.storyblok.com";
  } catch {
    return false;
  }
}
